import { createContext, type ReactNode, useContext, useState } from 'react';

/** Breadcrumb segment: label only for the leaf, label + href for navigable parents. */
export type Crumb = { label: string; href?: string };

export type PageVariant = 'form' | 'wide' | 'canvas';

/** The active page's top-bar chrome, registered by <Page>. */
export interface PageChrome {
    title: string;
    breadcrumbs?: Crumb[];
    /** Staff-only page — renders a "Staff" badge next to the title. */
    staff?: boolean;
    /** Body format; canvas fills the content area without gutters. */
    variant?: PageVariant;
}

// Split into value + setter contexts so the setter identity is stable (the
// useState setter never changes), letting <Page> consume it without re-rendering
// every time the chrome value updates.
const ChromeValueContext = createContext<PageChrome | null>(null);
const ChromeSetterContext = createContext<((chrome: PageChrome | null) => void) | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }) {
    const [chrome, setChrome] = useState<PageChrome | null>(null);
    return (
        <ChromeSetterContext.Provider value={setChrome}>
            <ChromeValueContext.Provider value={chrome}>{children}</ChromeValueContext.Provider>
        </ChromeSetterContext.Provider>
    );
}

/** Top-bar consumer: the active page's title + breadcrumb trail, or null. */
export function usePageChrome(): PageChrome | null {
    return useContext(ChromeValueContext);
}

const noopSetChrome = (_chrome: PageChrome | null): void => {};

/**
 * Setter for <Page> to register/clear its chrome. Stable identity.
 * Outside a provider (e.g. a page rendered in isolation in a unit test) this is a
 * no-op — the real app always mounts PageChromeProvider at the root.
 */
export function usePageChromeSetter(): (chrome: PageChrome | null) => void {
    return useContext(ChromeSetterContext) ?? noopSetChrome;
}

/**
 * Body headings must start at <h2>: the page <h1> is the breadcrumb leaf that
 * AppLayout renders in the top bar, so one in the content area is a duplicate
 * title (IX-4244). Returns the offending headings' text, empty when clean.
 *
 * Scoped to the content root on purpose — the top bar's own <h1> lives in
 * <header>, outside it, and full-screen surfaces mounted outside AppLayout
 * (auth, onboarding) are never passed here.
 */
export function findBodyH1Titles(contentRoot: ParentNode | null): string[] {
    if (!contentRoot) return [];
    return Array.from(contentRoot.querySelectorAll('h1'), (el) => el.textContent?.trim() ?? '');
}
